/**
 * Dictionary access and locale-aware path helpers.
 *
 * Dictionaries are imported statically rather than through `import()` so every
 * localized page stays fully static — the site pre-renders ~13,000 pages and a
 * dynamic import here would push the localized routes onto a server runtime for no
 * benefit. Sixteen small dictionaries cost far less than that.
 */
import ar from "@/lib/i18n/dictionaries/ar";
import de from "@/lib/i18n/dictionaries/de";
import en, { type Dictionary } from "@/lib/i18n/dictionaries/en";
import es from "@/lib/i18n/dictionaries/es";
import fr from "@/lib/i18n/dictionaries/fr";
import hi from "@/lib/i18n/dictionaries/hi";
import id from "@/lib/i18n/dictionaries/id";
import it from "@/lib/i18n/dictionaries/it";
import ja from "@/lib/i18n/dictionaries/ja";
import ko from "@/lib/i18n/dictionaries/ko";
import nl from "@/lib/i18n/dictionaries/nl";
import pl from "@/lib/i18n/dictionaries/pl";
import pt from "@/lib/i18n/dictionaries/pt";
import ru from "@/lib/i18n/dictionaries/ru";
import tr from "@/lib/i18n/dictionaries/tr";
import vi from "@/lib/i18n/dictionaries/vi";
import zh from "@/lib/i18n/dictionaries/zh";
import {
  ALL_LOCALES,
  DEFAULT_LOCALE,
  getLocaleMeta,
  type LocaleMeta,
} from "@/lib/i18n/locales";

export type { Dictionary } from "@/lib/i18n/dictionaries/en";

const DICTIONARIES: Record<string, Dictionary> = {
  en,
  es,
  pt,
  fr,
  de,
  it,
  nl,
  pl,
  tr,
  ru,
  id,
  vi,
  hi,
  ja,
  ko,
  zh,
  ar,
};

/** The dictionary for a locale, falling back to English for an unknown code. */
export function getDictionary(locale: string): Dictionary {
  return DICTIONARIES[locale] ?? en;
}

/** Every locale that actually has a dictionary — what the switcher may offer. */
export function getTranslatedLocales(): LocaleMeta[] {
  return ALL_LOCALES.filter((locale) => Boolean(DICTIONARIES[locale.code]));
}

/** Leading two-letter path segment, i.e. a candidate locale prefix. */
const LEADING_LOCALE_REGEX = /^\/([a-z]{2})(?=\/|$)/;

/**
 * `t()` itself lives in `interpolate.ts`, which imports nothing — a client component
 * that needs it must not pull all 17 dictionaries in with it. Re-exported here so the
 * existing `import { t } from "@/lib/i18n"` call sites are unaffected.
 */
// biome-ignore lint/performance/noBarrelFile: one named re-export, not a barrel — `t()` has to be importable without the dictionaries, and moving 21 call sites onto a second import path to say so would be worse.
export { t } from "@/lib/i18n/interpolate";

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/**
 * Prefix a site path with its locale.
 *
 * English is prefixed like every other language: `localePath("en", "/apa")` is
 * `/en/apa`, and that is the canonical address of the page. Un-prefixed URLs are not
 * a second address for the same page — they are permanent redirects into this tree
 * (see `src/proxy.ts`), so nothing should ever link to one.
 */
export function localePath(locale: string, path = "/"): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized === "/" ? `/${locale}` : `/${locale}${normalized}`;
}

/**
 * Strip a locale prefix back off, yielding the un-prefixed path used as the key for
 * translations, hreflang sets and the route registry. `/en/apa` and `/es/apa` both
 * give `/apa`.
 */
export function unprefixedPath(path: string): string {
  const match = LEADING_LOCALE_REGEX.exec(path);
  const code = match?.[1];
  if (code && getLocaleMeta(code)) {
    return path.slice(code.length + 1) || "/";
  }
  return path || "/";
}

/**
 * Format a number the way the locale writes it: 10,834 in English, 10.834 in German,
 * 10 834 in French. Every localized page quotes the style count, so a hard-coded
 * "en-US" here is an English number sitting in German prose on 592 pages.
 *
 * Uses the BCP 47 tag rather than the route code, since `Intl` does not know "zh" means
 * Simplified Chinese while "zh-Hans" does.
 */
export function formatCount(locale: string, value: number): string {
  const meta = getLocaleMeta(locale);
  try {
    return value.toLocaleString(meta?.hreflang ?? "en-US");
  } catch {
    // An Intl build without this locale's data must not take the page down.
    return value.toLocaleString("en-US");
  }
}

/** `og:locale` for a locale code — `language_TERRITORY`, not the hreflang tag. */
export function ogLocale(locale: string): string {
  return getLocaleMeta(locale)?.ogLocale ?? "en_US";
}

/**
 * The locale a path belongs to, and the path with its locale prefix removed.
 *
 * `/en/apa` splits to English now rather than falling through to the default branch:
 * English is a routed locale with a real prefix, so a path that names it is answering
 * the question, not declining to.
 */
export function splitLocalePath(path: string): {
  locale: string;
  rest: string;
} {
  const match = LEADING_LOCALE_REGEX.exec(path);
  const code = match?.[1];
  if (code && getLocaleMeta(code)) {
    const rest = path.slice(code.length + 1) || "/";
    return { locale: code, rest };
  }
  return { locale: DEFAULT_LOCALE, rest: path };
}
